import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { auth, User } from './api';

interface AuthState {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Gate the UI with the global loading splash only on the FIRST auth check.
  // Later refreshes (e.g. after the rights-acknowledgement POST, or a plan
  // change) must NOT flip `loading` back on — otherwise <Protected> swaps the
  // active page for <Splash/> mid-flow, unmounting it and wiping its state.
  // That's what dropped the Studio's decoded track + selection when a signed-in
  // user accepted the rights modal on their first separation.
  const initializedRef = useRef(false);
  const refresh = useCallback(async () => {
    if (!initializedRef.current) setLoading(true);
    try {
      const u = await auth.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      initializedRef.current = true;
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      /* ignore */
    }
    setUser(null);
    window.location.href = '/login';
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, logout }}>{children}</AuthCtx.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth must be inside AuthProvider');
  return v;
}
